import { defineStore } from "pinia";
import cloneDeep from "lodash/cloneDeep";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { i18n } from "@/plugins/i18n";
import { SnackbarTypes, useSnackbarStore } from "@/_store/snackbar.module";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { useWorkspaceStore } from "@/_store/workspace.module";
import { StoreModule } from "@/constants/store";
import {
  AnalyticsCoverage,
  NETWORK_ANALYTICS_PERIODS,
  NetworkAnalyticsPeriod,
  NetworkAnalyticsRegion,
  NetworkConnectionAction,
  NetworkConnectionActionStatus,
  NetworkConnectionStatus,
  NetworkConnectionType,
  NetworkDestinationSwgStatus,
  NetworkGeoPrecision,
  NetworkMapNodeRole,
  NetworkTrendDirection,
} from "@/constants/network";
import type { Pagination } from "@/types";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Outcome of a revoke/connect. The flag is written synchronously, so there is no pending state. */
export interface NetworkConnectionActionResult {
  actionId: string;
  connectionId: string;
  type: NetworkConnectionAction;
  status: NetworkConnectionActionStatus;
  message: string | null;
}

/**
 * Freshness and quality descriptor attached to every analytics response. The board reads a periodic
 * snapshot rather than live data, so `dataAsOf` must be surfaced, and `coverage` decides whether a
 * widget renders, greys out or explains itself.
 */
export interface NetworkAnalyticsMeta {
  dataAsOf: number;
  historyStartsAt: number;
  partial: boolean;
  coverage: Record<string, AnalyticsCoverage>;
  unlocatedCount: number;
  excludedSessionKinds: string[];
  swgCoverageNote: string | null;
}

export interface NetworkTrend {
  changePercent: number | null;
  direction: NetworkTrendDirection | null;
  previousTotal: number | null;
}

export interface NetworkAnalyticsSummary {
  totalConnections: number;
  activeConnections: number;
  uniqueDevices: number;
  uniqueUsers: number;
  totalTxBytes: number | null;
  totalRxBytes: number | null;
  byType: Array<{ type: NetworkConnectionType; count: number }>;
  trend: NetworkTrend | null;
  avgLatencyMs: number | null;
  peakBandwidthBps: number | null;
  meta: NetworkAnalyticsMeta;
}

export interface NetworkMapNode {
  id: string;
  role: NetworkMapNodeRole;
  type: NetworkConnectionType;
  latitude: number | null;
  longitude: number | null;
  label: string | null;
  city: string | null;
  countryCode: string | null;
  region: NetworkAnalyticsRegion | null;
  count: number;
  precision: NetworkGeoPrecision;
}

export interface NetworkMapArc {
  fromNodeId: string;
  toNodeId: string;
  type: NetworkConnectionType;
  count: number;
}

export interface NetworkAnalyticsMap {
  nodes: NetworkMapNode[];
  arcs: NetworkMapArc[];
  meta: NetworkAnalyticsMeta;
}

export interface NetworkRegionCount {
  region: NetworkAnalyticsRegion;
  count: number;
}

/** `count` is null outside the window the backend actually holds — render a gap, never a zero. */
export interface NetworkTimeseriesPoint {
  bucketStart: number;
  count: number | null;
  activeCount: number | null;
}

export interface NetworkAnalyticsTimeseries {
  granularity: string;
  items: NetworkTimeseriesPoint[];
  trend: NetworkTrend | null;
  meta: NetworkAnalyticsMeta;
}

export interface NetworkConnection {
  id: string;
  type: NetworkConnectionType;
  sessionKind: string | null;
  email: string | null;
  agentId: string | null;
  hostname: string | null;
  deviceModel: string | null;
  osType: string | null;
  authType: string | null;
  clientIp: string | null;
  tunnelIp: string | null;
  serverIp: string | null;
  region: NetworkAnalyticsRegion | null;
  startTime: number | null;
  endTime: number | null;
  durationSeconds: number | null;
  status: NetworkConnectionStatus;
  terminateCause: string | null;
  txBytes: number | null;
  rxBytes: number | null;
  bandwidthBps: number | null;
  bandwidthBasis: string | null;
  latencyMs: number | null;
  geo: {
    latitude: number | null;
    longitude: number | null;
    city: string | null;
    countryCode: string | null;
    precision: NetworkGeoPrecision;
  } | null;
  hasDestinations: boolean;
  canRevoke: boolean;
  canConnect: boolean;
}

export interface NetworkDestination {
  id: string;
  domain: string;
  swgStatus: NetworkDestinationSwgStatus;
  hits: number;
  lastSeen: number | null;
  dnsReplyTimeMs: number | null;
  destinationIp: string | null;
  port: number | null;
  protocol: string | null;
  txBytes: number | null;
  rxBytes: number | null;
}

/** Window every widget and the table report on, in epoch milliseconds. */
export interface NetworkAnalyticsTimeFrame {
  fromTime: number;
  toTime: number;
}

/**
 * Which parts of the board this environment serves, as configured on the backend.
 *
 * `boardVisible` is computed there rather than here: a board whose every section is switched off is not
 * a board, and that rule belongs with the flags it is derived from.
 */
export interface NetworkAnalyticsFeatures {
  boardVisible: boolean;
  map: boolean;
  regions: boolean;
  timeseries: boolean;
  connections: boolean;
  connectionDetails: boolean;
}

/**
 * Assumed while the real flags are in flight, and after a failed request.
 *
 * Nothing is shown until the backend has answered, so a slow or broken flags call cannot flash a section
 * that this environment does not serve — the opposite default would leak an unreleased feature.
 */
export const NETWORK_ANALYTICS_FEATURES_HIDDEN: NetworkAnalyticsFeatures = {
  boardVisible: false,
  map: false,
  regions: false,
  timeseries: false,
  connections: false,
  connectionDetails: false,
};

export interface NetworkAnalyticsState {
  features: NetworkAnalyticsFeatures | null;
  /**
   * Whether the flags request has completed successfully. Tracked apart from `features` so a
   * transient failure is not cached as though it were an answer: the hidden sentinel is still stored
   * as the value, and the next caller retries.
   */
  featuresLoaded: boolean;
  /** Workspace the cached flags belong to. They are per-workspace, and a `?workspaceId=` switch resets nothing. */
  featuresWorkspaceId: string;
  summary: NetworkAnalyticsSummary | null;
  map: NetworkAnalyticsMap | null;
  regions: NetworkRegionCount[];
  timeseries: NetworkAnalyticsTimeseries | null;
  connections: NetworkConnection[];
  connectionsTotal: number;
  /** Destination rows are fetched lazily on first expand and cached per connection. */
  destinations: Record<string, NetworkDestination[]>;
  destinationsLoading: Record<string, boolean>;
  pagination: Pagination;
  sortingOptions?: string;
  period: NetworkAnalyticsPeriod;
  timeFrame: NetworkAnalyticsTimeFrame;
  showSkeletonLoader: boolean;
  connectionsLoading: boolean;
}

const defaultNetworkAnalyticsState: NetworkAnalyticsState = {
  features: null,
  featuresLoaded: false,
  featuresWorkspaceId: "",
  summary: null,
  map: null,
  regions: [],
  timeseries: null,
  connections: [],
  connectionsTotal: 0,
  destinations: {},
  destinationsLoading: {},
  pagination: {
    page: 0,
    pageSize: 25,
  },
  sortingOptions: "startTime,desc",
  period: NetworkAnalyticsPeriod.MONTH,
  // Resolved on first use rather than at module load, so it can never be a window from an earlier session.
  timeFrame: { fromTime: 0, toTime: 0 },
  showSkeletonLoader: true,
  connectionsLoading: false,
};

export const useNetworkAnalyticsStore = defineStore(StoreModule.NETWORK_ANALYTICS, {
  // Deep-cloned so the arrays and objects on the defaults are never shared with the live state, which
  // an in-place mutation would otherwise write into and leak between resets and between test files.
  state: (): NetworkAnalyticsState => cloneDeep(defaultNetworkAnalyticsState),
  getters: {
    /** Never null, so callers can read a flag without first checking whether they arrived. */
    enabledFeatures(state): NetworkAnalyticsFeatures {
      return state.features ?? NETWORK_ANALYTICS_FEATURES_HIDDEN;
    },
  },
  actions: {
    /**
     * Recomputes the reported window from the selected period.
     *
     * Held in state rather than derived in a getter: a getter is a `computed`, and `Date.now()` is not
     * reactive, so it would freeze at whatever the clock read the first time it was touched. Keeping it
     * in state also means pagination and a lazily expanded row report on exactly the window the rest of
     * the board was drawn for, instead of each request inventing its own.
     */
    refreshTimeFrame(): NetworkAnalyticsTimeFrame {
      const toTime = Date.now();
      this.timeFrame = {
        fromTime: toTime - NETWORK_ANALYTICS_PERIODS[this.period].days * DAY_MS,
        toTime,
      };

      return this.timeFrame;
    },

    /** The window in force, resolved on first use so no caller can read an unset one. */
    currentTimeFrame(): NetworkAnalyticsTimeFrame {
      return this.timeFrame.toTime > 0 ? this.timeFrame : this.refreshTimeFrame();
    },

    /**
     * Reads the rollout flags. Cached per workspace for the session: they come from the backend's static
     * configuration, and the navigation guard awaits this on every entry to the tab, so re-fetching would
     * put a request in front of each navigation for a value that cannot have changed.
     *
     * The cache is keyed on the workspace because the flags are not global, and the `?workspaceId=` switch
     * path resets no stores — without the key an admin could be admitted to the board of a workspace that
     * does not serve it, on the strength of the previous workspace's answer.
     */
    async getFeatures(force = false): Promise<NetworkAnalyticsFeatures> {
      const workspaceId = useWorkspaceStore().workspace.workspaceId;
      if (this.featuresLoaded && this.featuresWorkspaceId === workspaceId && !force) {
        return this.enabledFeatures;
      }
      try {
        const { data } = await axiosInstance.request<NetworkAnalyticsFeatures>({
          ...api.networkAnalyticsFeatures,
        });
        this.features = data;
        this.featuresWorkspaceId = workspaceId;
        this.featuresLoaded = true;
      } catch (e) {
        console.error(e);
        // Hiding the board is the safe failure: showing a section this environment does not serve would
        // leave the user with widgets that can only ever error. Deliberately not marked as loaded, so a
        // transient failure is retried on the next entry rather than hiding the tab for the session.
        this.features = { ...NETWORK_ANALYTICS_FEATURES_HIDDEN };
        this.featuresLoaded = false;
      }

      return this.enabledFeatures;
    },

    /**
     * Loads the three widgets in parallel. Each request falls back to its previous value on failure so
     * one unavailable widget cannot blank the whole tab.
     */
    async getOverview() {
      this.showSkeletonLoader = true;
      const { fromTime, toTime } = this.refreshTimeFrame();
      const features = await this.getFeatures();
      if (!features.boardVisible) {
        this.showSkeletonLoader = false;
        return;
      }
      try {
        // A section that is switched off is not requested at all: fetching data for a widget that will
        // never be rendered costs a round trip and, in an environment that has not enabled it, is exactly
        // the request most likely to fail.
        const [summary, map, regions, timeseries] = await Promise.all([
          axiosInstance
            .request<NetworkAnalyticsSummary>({
              ...api.networkAnalyticsSummary({ fromTime, toTime }),
            })
            .then((response) => response.data)
            .catch(() => null),
          features.map
            ? axiosInstance
                .request<NetworkAnalyticsMap>({ ...api.networkAnalyticsMap({ fromTime, toTime }) })
                .then((response) => response.data)
                .catch(() => null)
            : Promise.resolve(null),
          features.regions
            ? axiosInstance
                .request<{ items: NetworkRegionCount[] }>({
                  ...api.networkAnalyticsRegions({ fromTime, toTime }),
                })
                .then((response) => response.data?.items ?? [])
                .catch(() => [])
            : Promise.resolve([] as NetworkRegionCount[]),
          features.timeseries
            ? axiosInstance
                .request<NetworkAnalyticsTimeseries>({
                  ...api.networkAnalyticsTimeseries({
                    fromTime,
                    toTime,
                    granularity: NETWORK_ANALYTICS_PERIODS[this.period].granularity,
                  }),
                })
                .then((response) => response.data)
                .catch(() => null)
            : Promise.resolve(null),
        ]);
        // A widget that failed keeps whatever it was already showing: blanking a populated card on a
        // period change would throw away good data and leave an empty card with nothing to explain it.
        this.summary = summary ?? this.summary;
        this.map = map ?? this.map;
        this.regions = regions.length ? regions : this.regions;
        this.timeseries = timeseries ?? this.timeseries;
      } catch (e) {
        console.error(e);
      } finally {
        this.showSkeletonLoader = false;
      }
    },

    async getConnections() {
      // Guarded here rather than only at the call sites: a disabled section must not reach the network
      // no matter who asks, both to save the round trip and because an environment that has not enabled
      // a section is the one whose endpoint is most likely to be unavailable.
      if (!this.enabledFeatures.connections) {
        return;
      }
      this.connectionsLoading = true;
      const { fromTime, toTime } = this.currentTimeFrame();
      // Read from the filters store rather than held here: that is where the filter row writes them and
      // where they are persisted across navigation, and one owner is what keeps the box the user sees and
      // the query that is sent from drifting apart.
      const filters = useFiltersStore().filters[FilterContext.NETWORK_ANALYTICS_CONNECTIONS];
      try {
        const request = {
          ...api.networkAnalyticsConnections({
            fromTime,
            toTime,
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
            sort: this.sortingOptions,
            // Send null rather than an empty string: the query serializer drops nulls entirely.
            search: filters.search || null,
            status: filters.status,
          }),
        };
        const { data } = await axiosInstance.request<{
          items: NetworkConnection[];
          total: number;
        }>(request);
        this.connections = data?.items ?? [];
        this.connectionsTotal = data?.total ?? 0;
      } catch (e) {
        console.error(e);
        this.connections = [];
        this.connectionsTotal = 0;
      } finally {
        this.connectionsLoading = false;
      }
    },

    /**
     * Fetched on first expand only. The rows come from a different store with its own freshness and are
     * unbounded per connection, so loading them with the table would be wasted work for every row the
     * user never opens.
     */
    async getDestinations(connectionId: string) {
      if (
        !this.enabledFeatures.connectionDetails ||
        this.destinations[connectionId] ||
        this.destinationsLoading[connectionId]
      ) {
        return;
      }
      this.destinationsLoading = { ...this.destinationsLoading, [connectionId]: true };
      const { fromTime, toTime } = this.currentTimeFrame();
      try {
        const request = {
          ...api.networkAnalyticsDestinations(connectionId, {
            fromTime,
            toTime,
            page: 0,
            pageSize: 25,
            sort: "hits,desc",
          }),
        };
        const { data } = await axiosInstance.request<{ items: NetworkDestination[] }>(request);
        this.destinations = { ...this.destinations, [connectionId]: data?.items ?? [] };
      } catch (e) {
        console.error(e);
        this.destinations = { ...this.destinations, [connectionId]: [] };
      } finally {
        this.destinationsLoading = { ...this.destinationsLoading, [connectionId]: false };
      }
    },

    /**
     * Drops the lazily loaded destination rows.
     *
     * They were fetched for one window and one connection state, and are cached by connection id alone,
     * so anything that moves either has to clear them: re-expanding a row would otherwise answer from
     * the cache and show detail that quietly disagrees with the rest of the board.
     */
    clearDestinations() {
      this.destinations = {};
      this.destinationsLoading = {};
    },

    async revokeConnection(connectionId: string) {
      await this.applyConnectionAction(
        api.revokeNetworkAnalyticsConnection(connectionId),
        "network.overview.snackbar.revoked"
      );
    },

    async connectConnection(connectionId: string) {
      await this.applyConnectionAction(
        api.connectNetworkAnalyticsConnection(connectionId),
        "network.overview.snackbar.connected"
      );
    },

    /**
     * The backend writes the flag synchronously and reports the outcome, so the result is read rather than
     * assumed: reporting success for a device it could not find would tell the admin the fleet is in a state
     * it is not in.
     */
    async applyConnectionAction(request: object, successKey: string) {
      const snackbar = useSnackbarStore();
      const failed = () =>
        snackbar.add({
          html: i18n.global.t("network.overview.snackbar.actionFailed"),
          type: SnackbarTypes.ERROR,
        });
      try {
        const { data } = await axiosInstance.request<NetworkConnectionActionResult>({ ...request });
        if (data?.status === NetworkConnectionActionStatus.FAILED) {
          failed();
          return;
        }
        snackbar.addGenericSuccess(i18n.global.t(successKey));
        this.clearDestinations();
        await this.getConnections();
      } catch (e) {
        console.error(e);
        failed();
      }
    },

    setPagination(pagination: Pagination) {
      this.pagination = { ...pagination };
    },

    /** Back to the first page, keeping the rows-per-page the user chose. */
    resetPagination() {
      this.pagination = {
        ...this.pagination,
        page: defaultNetworkAnalyticsState.pagination.page,
      };
    },

    setSortingOptions(sort?: string) {
      this.sortingOptions = sort;
    },

    async setPeriod(period: NetworkAnalyticsPeriod) {
      if (this.period === period) {
        return;
      }
      this.period = period;
      this.resetPagination();
      this.clearDestinations();
      // Refreshed here rather than left to getOverview, so the table and the widgets are guaranteed to
      // report on the same window whichever of the two reaches the network first.
      this.refreshTimeFrame();
      await Promise.all([this.getOverview(), this.getConnections()]);
    },
  },
});
